import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DocumentTextIcon,
  FolderIcon,
  PencilIcon,
  PlusIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { MobileBottomSheet } from "../../utils/mobile-bottom-sheet";
import { PMDialog } from "../../utils/PMDialog";
import { ContactSection } from "./ContactSection";
import { EmployeeSection } from "./EmployeeSection";
import { ProjectContactsSection } from "./ProjectContactsSectionV2";

const labelClass =
  "max-w-full text-center text-[10px] font-medium leading-tight";

const itemClass = (active, disabled) =>
  `flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 transition-colors ${
    disabled
      ? "cursor-not-allowed text-gray-300"
      : active
        ? "bg-teal-50 text-teal-600 active:bg-teal-100"
        : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
  }`;

function findTabIndex(tabs, tabName) {
  return tabs.findIndex((tab) => tab.name === tabName);
}

function isTabAvailable(tabs, tabName) {
  const index = findTabIndex(tabs, tabName);
  return index >= 0 && tabs[index]?.active !== false;
}

function getActiveTabs(tabs) {
  return tabs
    .map((tab, index) => ({ tab, index }))
    .filter(({ tab }) => tab.active !== false);
}

/**
 * Fixed bottom navigation for project view on mobile/tablet.
 * Five nav slots when `hideActionsSlot` is set (create actions live on the FAB).
 * Otherwise six slots, with the last opening a create-actions sheet.
 */
export function ProjectMobileActionsNav({
  actions = [],
  tabs = [],
  selectedTab = 0,
  setSelectedTab,
  project,
  onRefresh,
  canManageContacts = false,
  hideActionsSlot = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [actionsSheetOpen, setActionsSheetOpen] = useState(false);
  const [tabsSheetOpen, setTabsSheetOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);

  const activeActions = actions.filter((action) => action.active);
  const activeTabs = useMemo(() => getActiveTabs(tabs), [tabs]);

  useEffect(() => {
    if (!project?.id) {
      document.documentElement.style.setProperty("--mobile-nav-height", "0px");
      return undefined;
    }

    const mq = window.matchMedia("(max-width: 1023px)");
    const syncNavHeight = () => {
      document.documentElement.style.setProperty(
        "--mobile-nav-height",
        mq.matches ? "3.75rem" : "0px"
      );
    };
    syncNavHeight();
    mq.addEventListener("change", syncNavHeight);
    return () => {
      mq.removeEventListener("change", syncNavHeight);
      document.documentElement.style.setProperty("--mobile-nav-height", "0px");
    };
  }, [project?.id]);

  const navItems = useMemo(() => {
    const items = [
      {
        key: "project",
        label: t("project.view.mobileNav.project"),
        icon: PencilIcon,
        tabName: "Detaljer",
      },
      {
        key: "tabs",
        label: t("project.view.mobileNav.tabs"),
        icon: Squares2X2Icon,
        opensTabsSheet: true,
      },
      {
        key: "contacts",
        label: t("Kontakter"),
        icon: UserGroupIcon,
        opensContacts: true,
      },
      {
        key: "drawings",
        label: t("project.view.mobileNav.drawings"),
        icon: DocumentTextIcon,
        tabName: "Tegninger",
      },
      {
        key: "folders",
        label: t("document_archive.tab"),
        icon: FolderIcon,
        tabName: t("document_archive.tab"),
      },
    ];
    if (!hideActionsSlot) {
      items.push({
        key: "more",
        label: t("project.view.mobileNav.more"),
        icon: PlusIcon,
        opensActionsSheet: true,
      });
    }
    return items;
  }, [hideActionsSlot, t]);

  const handleAction = (action) => {
    setActionsSheetOpen(false);
    if (action.action) {
      action.action();
    } else if (action.path) {
      navigate(action.path);
    }
  };

  const actionSheetItems = activeActions.map((action) => ({
    key: action.label,
    label: action.mobileLabel || action.label,
    description: action.description,
    icon: action.icon,
    iconForeground: action.iconForeground,
    iconBackground: action.iconBackground,
    onClick: () => handleAction(action),
  }));

  const tabSheetItems = activeTabs.map(({ tab, index }) => ({
    key: `${tab.name}-${index}`,
    label: tab.name,
    icon: tab.icon,
    iconForeground: "text-teal-700",
    iconBackground: "bg-teal-50",
    selected: selectedTab === index,
    onClick: () => {
      setTabsSheetOpen(false);
      setSelectedTab?.(index);
    },
  }));

  const handleNavClick = (item) => {
    if (item.opensActionsSheet) {
      if (actionSheetItems.length > 0) {
        setTabsSheetOpen(false);
        setActionsSheetOpen(true);
      }
      return;
    }

    if (item.opensTabsSheet) {
      if (activeTabs.length > 0) {
        setActionsSheetOpen(false);
        setTabsSheetOpen(true);
      }
      return;
    }

    if (item.opensContacts) {
      setActionsSheetOpen(false);
      setTabsSheetOpen(false);
      setContactsOpen(true);
      return;
    }

    setActionsSheetOpen(false);
    setTabsSheetOpen(false);

    const index = findTabIndex(tabs, item.tabName);
    if (index < 0 || tabs[index]?.active === false) {
      return;
    }
    setSelectedTab?.(index);
  };

  const mobileNav = (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
      style={{
        paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
      }}
      aria-label={t("project.view.mobileNav.ariaLabel")}
    >
      <div className="flex w-full items-stretch justify-around gap-0 px-0.5 pt-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const tabIndex = item.tabName
            ? findTabIndex(tabs, item.tabName)
            : -1;
          const available = item.opensActionsSheet
            ? actionSheetItems.length > 0
            : item.opensTabsSheet
              ? activeTabs.length > 0
              : item.opensContacts
                ? Boolean(project?.id)
                : isTabAvailable(tabs, item.tabName);
          const active = item.opensActionsSheet
            ? actionsSheetOpen
            : item.opensTabsSheet
              ? tabsSheetOpen
              : item.opensContacts
                ? contactsOpen
                : tabIndex >= 0 && selectedTab === tabIndex;

          return (
            <button
              key={item.key}
              type="button"
              disabled={!available}
              onClick={() => handleNavClick(item)}
              className={itemClass(active, !available)}
              title={item.label}
              aria-expanded={
                item.opensActionsSheet
                  ? actionsSheetOpen
                  : item.opensTabsSheet
                    ? tabsSheetOpen
                    : undefined
              }
              aria-haspopup={
                item.opensActionsSheet || item.opensTabsSheet
                  ? "menu"
                  : undefined
              }
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              <span className={labelClass}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );

  return (
    <>
      <MobileBottomSheet
        open={!hideActionsSlot && actionsSheetOpen}
        onClose={() => setActionsSheetOpen(false)}
        title={t("project.view.mobileActions.actionsTitle")}
        ariaLabel={t("project.view.mobileActions.actionsTitle")}
        items={actionSheetItems}
      />

      <MobileBottomSheet
        open={tabsSheetOpen}
        onClose={() => setTabsSheetOpen(false)}
        title={t("project.view.mobileActions.tabsTitle")}
        ariaLabel={t("project.view.mobileActions.tabsTitle")}
        items={tabSheetItems}
      />

      <PMDialog
        open={contactsOpen}
        onClose={() => setContactsOpen(false)}
        title={t("project.view.mobileNav.contactsTitle")}
        size="5xl"
        dense
        zIndex={2100}
        closeOnOutsideClick
      >
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pb-2">
          <ContactSection contactId={project?.contact?.id} />
          <EmployeeSection
            employeeId={project?.project_manager?.id}
            title={t("project.manager")}
          />
          <ProjectContactsSection
            project={project}
            onRefresh={onRefresh}
            canManage={canManageContacts}
          />
        </div>
      </PMDialog>

      {typeof document !== "undefined"
        ? createPortal(mobileNav, document.body)
        : mobileNav}
    </>
  );
}

export default ProjectMobileActionsNav;
